import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import ReturnToDashboardPill from '@/components/ReturnToDashboardPill';
import useDeepLinkParams from '@/hooks/useDeepLinkParams';
import { toast } from 'sonner';
import { 
  BookOpen, 
  Plus, 
  Trash, 
  PencilSimple, 
  ArrowsClockwise,
  Tag,
  Brain,
  Lightning,
  Copy,
  CaretDown,
  CaretUp,
  MagnifyingGlass,
  Sparkle
} from '@phosphor-icons/react';
import { getToken } from '@/utils/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BusinessBrainPage = () => {
  const navigate = useNavigate();
  const deepLink = useDeepLinkParams();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState({});
  const [filterType, setFilterType] = useState('all');
  const [filterScope, setFilterScope] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    note_type: 'general',
    source: '',
    applies_to: 'everywhere',
    tags: []
  });
  const [submitting, setSubmitting] = useState(false);

  const noteTypes = [
    { value: 'general', label: 'General', icon: BookOpen, bgClass: 'bg-blue-100', textClass: 'text-blue-600', tagBgClass: 'bg-blue-100', tagTextClass: 'text-blue-700' },
    { value: 'strategy', label: 'Strategy', icon: Lightning, bgClass: 'bg-purple-100', textClass: 'text-purple-600', tagBgClass: 'bg-purple-100', tagTextClass: 'text-purple-700' },
    { value: 'mindset', label: 'Mindset', icon: Brain, bgClass: 'bg-green-100', textClass: 'text-green-600', tagBgClass: 'bg-green-100', tagTextClass: 'text-green-700' },
    { value: 'marketing', label: 'Marketing', icon: Sparkle, bgClass: 'bg-pink-100', textClass: 'text-pink-600', tagBgClass: 'bg-pink-100', tagTextClass: 'text-pink-700' },
    { value: 'sales', label: 'Sales', icon: Tag, bgClass: 'bg-orange-100', textClass: 'text-orange-600', tagBgClass: 'bg-orange-100', tagTextClass: 'text-orange-700' },
    { value: 'content', label: 'Content', icon: Copy, bgClass: 'bg-cyan-100', textClass: 'text-cyan-600', tagBgClass: 'bg-cyan-100', tagTextClass: 'text-cyan-700' },
    { value: 'product', label: 'Product', icon: BookOpen, bgClass: 'bg-indigo-100', textClass: 'text-indigo-600', tagBgClass: 'bg-indigo-100', tagTextClass: 'text-indigo-700' }
  ];

  const appliesToOptions = [
    { 
      value: 'everywhere', 
      label: 'All Bots', 
      description: 'Use this guidance across Content, Money, and Growth bots',
      icon: '🌐',
      color: 'purple'
    },
    { 
      value: 'content_only', 
      label: 'Content Bot Only', 
      description: 'Only use when creating content, hooks, and scripts',
      icon: '✍️',
      color: 'cyan'
    },
    { 
      value: 'monetization_only', 
      label: 'Money Bot Only', 
      description: 'Only use for offers, pricing, and income planning',
      icon: '💰',
      color: 'green'
    },
    { 
      value: 'growth_only', 
      label: 'Growth Bot Only', 
      description: 'Only use for audience growth and viral strategies',
      icon: '📈',
      color: 'orange'
    }
  ];

  useEffect(() => {
    fetchNotes();
  }, [filterType, filterScope]);

  // Round 95 — deep-link acceptance from <TaskActionButton />.
  useEffect(() => {
    if (deepLink.hasDeepLink) {
      console.info('[BusinessBrain] deep-link accepted', deepLink);
    }
  }, [deepLink.hasDeepLink, deepLink.asset_id, deepLink.task_id]);

  const fetchNotes = async () => {
    try {
      const token = getToken();
      let url = `${API_URL}/api/business-brain/notes`;
      const params = new URLSearchParams();
      if (filterType !== 'all') {
        params.append('note_type', filterType);
      }
      if (filterScope !== 'all') {
        params.append('applies_to', filterScope);
      }
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch notes');
      
      const data = await response.json();
      setNotes(data);
    } catch (error) {
      console.error('Error fetching notes:', error);
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const token = getToken();
      const url = editingNote 
        ? `${API_URL}/api/business-brain/notes/${editingNote.id}`
        : `${API_URL}/api/business-brain/notes`;
      
      const method = editingNote ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) throw new Error('Failed to save note');
      
      toast.success(editingNote ? 'Note updated!' : 'Note saved & summarized!');
      resetForm();
      fetchNotes();
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error('Failed to save note');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/business-brain/notes/${noteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to delete note');
      
      toast.success('Note deleted');
      fetchNotes();
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note');
    }
  };

  const handleRegenerateSummary = async (noteId) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/business-brain/notes/${noteId}/regenerate-summary`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to regenerate summary');
      
      toast.success('Summary regenerated!');
      fetchNotes();
    } catch (error) {
      console.error('Error regenerating summary:', error);
      toast.error('Failed to regenerate summary');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      note_type: 'general',
      source: '',
      applies_to: 'everywhere',
      tags: []
    });
    setShowForm(false);
    setEditingNote(null);
  };

  const handleEdit = (note) => {
    setFormData({
      title: note.title,
      content: note.content,
      note_type: note.note_type,
      source: note.source || '',
      applies_to: note.applies_to,
      tags: note.tags || []
    });
    setEditingNote(note);
    setShowForm(true);
  };

  const toggleExpanded = (noteId) => {
    setExpandedNotes(prev => ({
      ...prev,
      [noteId]: !prev[noteId]
    }));
  };

  const getTypeInfo = (type) => {
    return noteTypes.find(t => t.value === type) || noteTypes[0];
  };

  const getAppliesToInfo = (appliesTo) => {
    const info = appliesToOptions.find(o => o.value === appliesTo);
    if (!info) return { label: 'All Bots', icon: '🌐', color: 'purple' };
    return info;
  };

  const filteredNotes = notes.filter(note => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      note.title.toLowerCase().includes(query) ||
      note.content.toLowerCase().includes(query) ||
      (note.summary && note.summary.toLowerCase().includes(query))
    );
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Round 95 — Return pill visible when arriving via deep link */}
        <div className="mb-4 flex items-center justify-between">
          <ReturnToDashboardPill testId="business-brain-return-pill" />
          {deepLink.hasDeepLink && (
            <span
              className="text-xs text-slate-500"
              data-testid="business-brain-deep-link-indicator"
              data-workflow-task-id={deepLink.task_id || ''}
              data-workflow-asset-id={deepLink.asset_id || ''}
              data-workflow-mode={deepLink.mode || ''}
              data-workflow-asset-type={deepLink.asset_type || ''}
            >
              Deep-linked from your Dashboard task
            </span>
          )}
        </div>
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2 flex items-center gap-3" data-testid="business-brain-title">
                <Brain size={40} weight="duotone" className="text-purple-600" />
                Business Brain
              </h1>
              <p className="text-slate-600">
                Save guidance from mentors and courses. Your bots will use these insights to personalize advice.
              </p>
            </div>
            <button
              onClick={() => setShowForm(true)}
              data-testid="add-note-btn"
              className="flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold transition-colors"
            >
              <Plus size={20} weight="bold" />
              Add Note
            </button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="mb-6 flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px] relative">
            <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-notes-input"
              className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            data-testid="filter-type-select"
            className="px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
          >
            <option value="all">All Types</option>
            {noteTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          <select
            value={filterScope}
            onChange={(e) => setFilterScope(e.target.value)}
            data-testid="filter-scope-select"
            className="px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
          >
            <option value="all">All Scopes</option>
            {appliesToOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.icon} {opt.label}</option>
            ))}
          </select>
        </div>

        {/* Add/Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-2xl font-bold text-slate-900" data-testid="form-title">
                  {editingNote ? 'Edit Note' : 'Add Mentor Note'}
                </h2>
                <p className="text-slate-600 mt-1">
                  Your note will be automatically summarized for bot context.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="e.g., Alex Hormozi's $100M Offers Framework"
                    data-testid="note-title-input"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Content */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Content *
                  </label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    placeholder="Paste your notes, key insights, or strategies here..."
                    data-testid="note-content-input"
                    rows={8}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {formData.content.length} / 50,000 characters
                  </p>
                </div>

                {/* Type and Source */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Type
                    </label>
                    <select
                      value={formData.note_type}
                      onChange={(e) => setFormData({...formData, note_type: e.target.value})}
                      data-testid="note-type-select"
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                    >
                      {noteTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Source (optional)
                    </label>
                    <input
                      type="text"
                      value={formData.source}
                      onChange={(e) => setFormData({...formData, source: e.target.value})}
                      placeholder="e.g., Course: XYZ, Mentor: John"
                      data-testid="note-source-input"
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Applies To - Enhanced Scope Selector */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">
                    Where should this guidance apply?
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {appliesToOptions.map(opt => {
                      const isSelected = formData.applies_to === opt.value;
                      const colorClasses = {
                        purple: { border: 'border-purple-500', bg: 'bg-purple-50', text: 'text-purple-700', ring: 'ring-purple-500' },
                        cyan: { border: 'border-cyan-500', bg: 'bg-cyan-50', text: 'text-cyan-700', ring: 'ring-cyan-500' },
                        green: { border: 'border-green-500', bg: 'bg-green-50', text: 'text-green-700', ring: 'ring-green-500' },
                        orange: { border: 'border-orange-500', bg: 'bg-orange-50', text: 'text-orange-700', ring: 'ring-orange-500' }
                      };
                      const colors = colorClasses[opt.color] || colorClasses.purple;
                      
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setFormData({...formData, applies_to: opt.value})}
                          data-testid={`applies-to-${opt.value}`}
                          className={`p-3 rounded-xl border-2 transition-all text-left ${
                            isSelected 
                              ? `${colors.border} ${colors.bg} ring-2 ${colors.ring}` 
                              : 'border-slate-200 hover:border-slate-300 bg-white'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-lg">{opt.icon}</span>
                            <span className={`font-semibold text-sm ${isSelected ? colors.text : 'text-slate-800'}`}>
                              {opt.label}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 leading-tight">
                            {opt.description}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-400 mt-2 italic">
                    💡 Tip: Use "All Bots" for general business principles. Use specific bots for targeted strategies.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    data-testid="cancel-btn"
                    className="px-6 py-3 text-slate-600 hover:bg-slate-100 rounded-xl font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="save-note-btn"
                    className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold transition-colors disabled:opacity-50"
                  >
                    {submitting ? 'Saving...' : (editingNote ? 'Update Note' : 'Save & Summarize')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Notes List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-slate-600">Loading notes...</p>
          </div>
        ) : filteredNotes.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
            <Brain size={64} weight="duotone" className="text-purple-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No notes yet</h3>
            <p className="text-slate-600 mb-6">
              {searchQuery ? 'No notes match your search.' : 'Add mentor insights to personalize your bot responses.'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => setShowForm(true)}
                data-testid="empty-add-note-btn"
                className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold transition-colors"
              >
                <Plus size={20} weight="bold" />
                Add Your First Note
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredNotes.map(note => {
              const typeInfo = getTypeInfo(note.note_type);
              const TypeIcon = typeInfo.icon;
              const isExpanded = expandedNotes[note.id];

              return (
                <div
                  key={note.id}
                  data-testid={`note-card-${note.id}`}
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"
                >
                  {/* Note Header */}
                  <div 
                    className="p-5 cursor-pointer"
                    onClick={() => toggleExpanded(note.id)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`p-2 rounded-lg ${typeInfo.bgClass}`}>
                          <TypeIcon size={24} weight="duotone" className={typeInfo.textClass} />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-slate-900" data-testid={`note-title-${note.id}`}>
                            {note.title}
                          </h3>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <span className={`text-xs px-2 py-1 rounded-full ${typeInfo.tagBgClass} ${typeInfo.tagTextClass}`}>
                              {typeInfo.label}
                            </span>
                            {(() => {
                              const scopeInfo = getAppliesToInfo(note.applies_to);
                              const scopeColors = {
                                purple: 'bg-purple-100 text-purple-700',
                                cyan: 'bg-cyan-100 text-cyan-700',
                                green: 'bg-green-100 text-green-700',
                                orange: 'bg-orange-100 text-orange-700'
                              };
                              return (
                                <span 
                                  className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${scopeColors[scopeInfo.color] || 'bg-purple-100 text-purple-700'}`}
                                  data-testid={`note-scope-${note.id}`}
                                >
                                  <span>{scopeInfo.icon}</span>
                                  <span>{scopeInfo.label}</span>
                                </span>
                              );
                            })()}
                            {note.source && (
                              <span className="text-xs text-slate-400">
                                via {note.source}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEdit(note); }}
                          data-testid={`edit-note-${note.id}`}
                          className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <PencilSimple size={20} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRegenerateSummary(note.id); }}
                          data-testid={`regenerate-note-${note.id}`}
                          className="p-2 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          title="Regenerate Summary"
                        >
                          <ArrowsClockwise size={20} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(note.id); }}
                          data-testid={`delete-note-${note.id}`}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash size={20} />
                        </button>
                        {isExpanded ? (
                          <CaretUp size={20} className="text-slate-400" />
                        ) : (
                          <CaretDown size={20} className="text-slate-400" />
                        )}
                      </div>
                    </div>

                    {/* Summary (always visible) */}
                    {note.summary && (
                      <div className="mt-3 p-3 bg-purple-50 rounded-lg">
                        <p className="text-sm text-purple-900 font-medium mb-1">AI Summary:</p>
                        <p className="text-sm text-purple-700">{note.summary}</p>
                      </div>
                    )}

                    {/* Key Takeaways */}
                    {note.key_takeaways && note.key_takeaways.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-slate-500 mb-2">Key Takeaways:</p>
                        <ul className="space-y-1">
                          {note.key_takeaways.slice(0, isExpanded ? undefined : 3).map((takeaway, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                              <span className="text-purple-500 mt-1">•</span>
                              {takeaway}
                            </li>
                          ))}
                        </ul>
                        {!isExpanded && note.key_takeaways.length > 3 && (
                          <p className="text-xs text-slate-400 mt-1">
                            +{note.key_takeaways.length - 3} more...
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="px-5 pb-5 pt-0 border-t border-slate-100">
                      <div className="pt-4">
                        <p className="text-xs font-medium text-slate-500 mb-2">Original Content:</p>
                        <div className="bg-slate-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">
                            {note.content}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Info Box */}
        <div className="mt-8 p-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl border border-purple-100">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-purple-100 rounded-xl">
              <Sparkle size={28} weight="duotone" className="text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-1">How Business Brain Works</h3>
              <p className="text-sm text-slate-600">
                When you save a note, AI automatically summarizes it into key takeaways. 
                Your Content Bot, Money Bot, and Growth Bot will use these summaries to 
                give you personalized advice aligned with your mentor's guidance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessBrainPage;
